import { logger } from "@/utils/logger";
import { profileDisplayName } from "@/stores/documents/repository/mappers";
import { isMissingRelation } from "@/stores/pm/repository";
import type {
  BriefVersion,
  BriefVersionDeletion,
  BriefVersionPayload,
  UnconfirmedChanges,
} from "../types";
import { NO_APPENDIX_CHANGES, NO_FOLDER_CHANGES } from "../briefDiff";
import { client, raise } from "./client";

interface VersionRow {
  id: number;
  brief_id: number;
  version_no: number;
  previous_version_id: number | null;
  note: string | null;
  payload: BriefVersionPayload;
  document_count: number;
  appendix_count: number;
  created_at: string;
  created_by: number;
  confirmed_by_profile: { first_name: string | null; last_name: string | null } | null;
}

const VERSION_COLUMNS =
  "id, brief_id, version_no, previous_version_id, note, payload, document_count, appendix_count, " +
  "created_at, created_by, confirmed_by_profile:user_profiles!project_brief_versions_created_by_fk(first_name, last_name)";

export async function fetchBriefVersions(briefId: number): Promise<BriefVersion[]> {
  const { data, error } = await client()
    .rpc("brief_get_versions", { p_brief_id: briefId })
    .select(VERSION_COLUMNS)
    .order("version_no", { ascending: false });
  if (error) raise(error);
  return ((data ?? []) as unknown as VersionRow[]).map(({ confirmed_by_profile, ...row }) => ({
    ...row,
    confirmed_by_name: profileDisplayName(confirmed_by_profile),
  }));
}

interface VersionDeletionRow {
  id: number;
  brief_id: number;
  version_id: number;
  version_no: number;
  note: string | null;
  document_count: number;
  appendix_count: number;
  confirmed_at: string;
  deleted_at: string;
  confirmed_by_profile: { first_name: string | null; last_name: string | null } | null;
  deleted_by_profile: { first_name: string | null; last_name: string | null } | null;
}

const VERSION_DELETION_SCALAR_COLUMNS =
  "id, brief_id, version_id, version_no, note, document_count, appendix_count, confirmed_at, deleted_at";
const VERSION_DELETION_COLUMNS =
  VERSION_DELETION_SCALAR_COLUMNS +
  ", " +
  "confirmed_by_profile:user_profiles!confirmed_by(first_name, last_name), " +
  "deleted_by_profile:user_profiles!deleted_by(first_name, last_name)";

/**
 * Deleted versions, newest deletion first — or `null` before the pipeline
 * migration that adds the table (and `brief_delete_version`) has applied:
 * nothing can have been deleted then, and the page offers no Delete and
 * subscribes to no table that is not there yet.
 */
export async function fetchBriefVersionDeletions(
  briefId: number
): Promise<BriefVersionDeletion[] | null> {
  let { data, error } = await client()
    .from("project_brief_version_deletions")
    .select(VERSION_DELETION_COLUMNS)
    .eq("brief_id", briefId)
    .order("deleted_at", { ascending: false })
    .returns<VersionDeletionRow[]>();
  if (error?.code === "PGRST200" || error?.code === "PGRST201") {
    void logger.warn(
      "brief",
      "brief.version_deletions.profile_fallback",
      "Deletion actor profiles unavailable; fetching deletion history without names",
      {
        briefId,
        code: error.code,
      }
    );
    ({ data, error } = await client()
      .from("project_brief_version_deletions")
      .select(VERSION_DELETION_SCALAR_COLUMNS)
      .eq("brief_id", briefId)
      .order("deleted_at", { ascending: false })
      .returns<VersionDeletionRow[]>());
  }
  if (error && isMissingRelation(error)) return null;
  if (error) raise(error);
  return ((data ?? []) as unknown as VersionDeletionRow[]).map(
    ({ confirmed_by_profile, deleted_by_profile, ...row }) => ({
      ...row,
      confirmed_by_name: profileDisplayName(confirmed_by_profile),
      deleted_by_name: profileDisplayName(deleted_by_profile),
    })
  );
}

/**
 * Deletes one confirmed version. The database records it in the brief's
 * history, re-links the version after it, and re-renders Franklin's additions
 * PDF (in `language`) when it was rendered from this version. Files are not
 * touched.
 */
export async function deleteBriefVersion(versionId: number, language: string): Promise<void> {
  const { error } = await client().rpc("brief_delete_version", {
    p_version_id: versionId,
    p_language: language === "en" ? "en" : "et",
  });
  if (error) raise(error);
}

/**
 * Confirms the next version: the brief's files and its additions as they are
 * now. `language` is the confirming person's: Franklin files the additions PDF
 * in it ("Täiendused.pdf" / "Additions.pdf").
 */
export async function confirmBriefVersion(
  briefId: number,
  note: string | null,
  language: string
): Promise<BriefVersion> {
  let { data, error } = await client().rpc("brief_confirm_version", {
    p_brief_id: briefId,
    p_note: note,
    // Estonian unless English was asked for; the database falls back the same way.
    p_language: language === "en" ? "en" : "et",
  });
  // Before the pipeline migration that added p_language, PostgREST finds no
  // function with it: confirm without, as before (no PDF is filed then).
  if (error?.code === "PGRST202") {
    ({ data, error } = await client().rpc("brief_confirm_version", {
      p_brief_id: briefId,
      p_note: note,
    }));
  }
  if (error) raise(error);
  const row = data as Omit<BriefVersion, "confirmed_by_name">;
  return { ...row, confirmed_by_name: null };
}

export async function fetchUnconfirmedChanges(briefId: number): Promise<UnconfirmedChanges> {
  const { data, error } = await client().rpc("brief_get_unconfirmed_changes", {
    p_brief_id: briefId,
  });
  if (error) raise(error);
  const raw = (data ?? {}) as Partial<UnconfirmedChanges>;
  return {
    ...unconfirmedDocumentChanges(raw),
    ...unconfirmedMetadata(raw),
    folders: raw.folders ?? NO_FOLDER_CHANGES,
    appendices: raw.appendices ?? NO_APPENDIX_CHANGES,
  };
}

function unconfirmedDocumentChanges(raw: Partial<UnconfirmedChanges>) {
  return { added: raw.added ?? [], removed: raw.removed ?? [], changed: raw.changed ?? [] };
}

function unconfirmedMetadata(raw: Partial<UnconfirmedChanges>) {
  return {
    since_version_id: raw.since_version_id ?? null,
    since_version_no: raw.since_version_no ?? null,
    document_count: raw.document_count ?? 0,
  };
}
